import { doctorsStore } from 'features/doctors/store/store';
import { observer } from 'mobx-react-lite';
import { DoctorCard } from '../doctorCard/doctorCard';
import { PreloaderWrapper, withPreloader } from 'shared/ui/preloader';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ITimeslot } from 'entities/timeslots/store/types';
import { Doctor } from 'features/doctors/store/types';
import styles from './styles.module.scss';
import { timeslotsStore } from 'entities/timeslots';
import { useParams } from 'react-router-dom';

const arePropsEqual = (
	prevProps: { doctor: Doctor; timeslot: ITimeslot | null },
	nextProps: { doctor: Doctor; timeslot: ITimeslot | null },
) => {
	return (
		prevProps.doctor === nextProps.doctor &&
		prevProps.timeslot === nextProps.timeslot
	);
};

const MemoizedDoctorCard = memo(DoctorCard, arePropsEqual);

const DoctorListLoader = withPreloader(PreloaderWrapper);
export const DoctorCardsList = observer(() => {
	const [timeslot, setTimeslot] = useState<ITimeslot | null>(null);
  const [ isFetched, setIsFetched ] = useState<boolean>(false);
  const { id } = useParams();

  const doctorsCardsList = useRef<HTMLDivElement | null>(null);

  const scrollToAppointmentButton = (count: number = 0) => {
    const button = document.getElementById('doctor-appointement-button');
    if (count > 1000||!button||button.classList.contains('inView')) return;

    const scroll = button.offsetTop > 0 ? 10 : -10;

    if (!id) {
      if (!doctorsCardsList.current) return;
      doctorsCardsList.current.scrollTo({
        top: doctorsCardsList.current.scrollTop + scroll,
        // behavior: 'smooth',
      })
    } else {
      window.scrollTo({
        top: window.scrollY + scroll,
        behavior: 'smooth',
      })
    }

    setTimeout(() => {
      count++
      scrollToAppointmentButton(count);
    }, 10);
  }

	const selectSlot = useCallback((slot: ITimeslot) => {
		setTimeslot(slot);
    setTimeout(() => {
      scrollToAppointmentButton()
    }, 10);
    // eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (doctorsStore.loading) {
			setTimeslot(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [doctorsStore.loading]);
  
  const fetchDoctor = useCallback(() => {
    if (id) {
      if (doctorsStore?.activeDoctor?.id !== parseInt(id)) {
        setIsFetched(false);
      }
      if (!isFetched) {
        setIsFetched(true);
        doctorsStore.fetchDoctorById(id);
      }
    }
  }, [id, setIsFetched, isFetched])

  useEffect(() => {
    if (id) {
      fetchDoctor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (id) {
    if (!doctorsStore.activeDoctor) {
      return <></>
    }

    return (
      <DoctorListLoader
        isLoading={doctorsStore.loading}
        error={doctorsStore.error}
      >
        <div 
          className={`${styles['doctors-cards-list']} ${id && styles['doctors-cards-list-autoheight']}`}
          ref={doctorsCardsList}
        >
          <div className="mb-2">
            <MemoizedDoctorCard
              key={id}
              doctor={doctorsStore.activeDoctor as Doctor}
              timeslot={timeslot}
              selectSlot={selectSlot}
            />
          </div>
        </div>
      </DoctorListLoader>
    )
  }

	return (
		<DoctorListLoader
			isLoading={doctorsStore.loading}
			error={doctorsStore.error}
		>
			<div 
        className={styles['doctors-cards-list']}
        ref={doctorsCardsList}
      >
				{doctorsStore.doctors.map((doctor, index) => {
					const slots = timeslotsStore.doctorsSlots.get(doctor.id);
					if (slots) {
						return (
							<div key={index} className="mb-2">
								<MemoizedDoctorCard
									key={doctor.id}
									doctor={doctor as Doctor}
									timeslot={timeslot}
									selectSlot={selectSlot}
								/>
							</div>
						);
					}
				})}
			</div>
		</DoctorListLoader>
	);
});
